import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { Persona } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { getAccessToken, getAgentPhoto } from "../../Services/GraphServices";
import {
  getAgentConnectedCalls,
  getAgentInformation,
  getMyAgentProfile,
} from "../../Services/HeedifyServices";
import { presenceLabel, presenceBadge } from "../../Utils/teamsPresence";

const AgentRow = ({
  agent,
  tenantId,
  agentPresence,
  connection,
  isConnected
}) => {
  const { t } = useTranslation();
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const userId = activeAccount?.localAccountId;
  
  const [agentData, setAgentData] = useState({
    photoUrl: null,
    connectedCalls: 0,
    totalCalls: 0,
    missedCalls: 0,
    totalCallsDuration: 0,
  });
  
  const [timezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  // Fonction pour obtenir le timezone numérique
  const getNumericTimezone = (ianaTimezone) => {
    const offsetMinutes = moment.tz(ianaTimezone).utcOffset();
    return Math.floor(offsetMinutes / 60);
  };
  const numericTimezone = getNumericTimezone(timezone);

  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return "00:00:00";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hrs.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Récupérer la photo de l'agent
  const fetchAgentPhoto = () => {
    getAccessToken(instance, activeAccount)
      .then((accessToken) => getAgentPhoto(agent.userId || agent.id, accessToken))
      .then((photo) => {
        setAgentData((prevDetails) => ({
          ...prevDetails,
          photoUrl: photo || null,
        }));
      })
      .catch((error) => {
        console.error(`🔵 [PHOTO ERROR] Agent ${agent.id}:`, error);
        setAgentData((prevDetails) => ({ ...prevDetails, photoUrl: null }));
      });
  };

  // Récupérer les appels connectés
  const fetchConnectedCalls = () => {
    getAccessToken(instance, activeAccount)
      .then((token) =>
        getAgentConnectedCalls(tenantId, agent.id, token).then((calls) => {
          const connectedCallsLength = calls?.connectedCalls
            ? Object.keys(calls.connectedCalls).length
            : 0;
          
          console.log(`🔵 [CONNECTED CALLS] Agent ${agent.id}:`, connectedCallsLength);
          
          setAgentData((prevDetails) => ({
            ...prevDetails,
            connectedCalls: connectedCallsLength,
          }));
        })
      )
      .catch((error) => {
        console.error(`❌ [CONNECTED CALLS ERROR] Agent ${agent.id}:`, error);
      });
  };

  // Récupérer les informations de l'agent
  const fetchAgentInformation = () => {
    getAccessToken(instance, activeAccount)
      .then((token) =>
        getMyAgentProfile(tenantId, userId, token).then((profile) =>
          getAgentInformation(
            tenantId,
            profile?.scope || 'default',
            agent.id,
            numericTimezone,
            true,
            token
          ).then((info) => {
            console.log(`🔵 [INITIAL LOAD] Agent ${agent.id} ):`, {
              totalCalls: info?.totalCalls || 0,
              missedCalls: info?.missedCalls || 0,
              totalCallsDuration: info?.totalCallsDuration || 0,
            });
            
            setAgentData((prevDetails) => ({
              ...prevDetails,
              totalCalls: info?.totalCalls || 0,
              missedCalls: info?.missedCalls || 0,
              totalCallsDuration: info?.totalCallsDuration || 0,
            }));
          })
        )
      )
      .catch((error) => {
        console.error(`❌ [INITIAL LOAD ERROR] Agent ${agent.id}:`, error);
        console.log(`🟡 [INITIAL LOAD FALLBACK] Agent ${agent.id}:`, {
          totalCalls: agent.todayCalls || 0,
          missedCalls: 0,
          totalCallsDuration: 0,
        });
        
        setAgentData((prevDetails) => ({
          ...prevDetails,
          totalCalls: agent.todayCalls || 0,
          missedCalls: 0,
          totalCallsDuration: 0,
        }));
      });
  };

  // Chargement initial
  useEffect(() => {
    if (agent.id) {
      fetchAgentPhoto();
      fetchConnectedCalls();
      fetchAgentInformation();
    }
  }, [agent.id]);

  // SignalR event listeners
  useEffect(() => {
    if (!connection || !isConnected || !agent.id) return;

    const connectedCallsEvent = `agentCalls${agent.id}`;
    const performanceEvent = `agentPerformance${agent.id}`;

    console.log(`🟢 [SIGNALR SETUP] Agent ${agent.id}: Registering listeners`);

    // Event pour les appels connectés
    const onConnectedCallsReceived = (result) => {
      console.log(`📞 [SIGNALR] ${connectedCallsEvent} received:`, result);
      
      if (result?.connectedCalls) {
        const connectedCallsLength = Object.keys(result.connectedCalls).length;
        console.log(`   ↳ Updating connectedCalls: ${connectedCallsLength}`);
        
        setAgentData((prevDetails) => ({
          ...prevDetails,
          connectedCalls: connectedCallsLength,
        }));
      }
    };

    // Event pour les données de performance
    const onPerformanceDataReceived = (result) => {
      console.log(`📊 [SIGNALR] ${performanceEvent} received:`, result);
      
      setAgentData((prevDetails) => {
        console.log(`   ↳ BEFORE update:`, prevDetails);
        
        if (result?.call) {
          const { isMissed, duration } = result.call;
          
          const newData = {
            ...prevDetails,
            totalCalls: (prevDetails.totalCalls || 0) + 1,
            missedCalls: isMissed
              ? (prevDetails.missedCalls || 0) + 1
              : prevDetails.missedCalls,
            totalCallsDuration: (prevDetails.totalCallsDuration || 0) + (duration || 0),
          };
          
          console.log(`   ↳ Call details: isMissed=${isMissed}, duration=${duration}`);
          console.log(`   ↳ AFTER update:`, newData);
          
          return newData;
        }
        return prevDetails;
      });
    };

    // Écouter les événements SignalR
    connection.on(connectedCallsEvent, onConnectedCallsReceived);
    connection.on(performanceEvent, onPerformanceDataReceived);

    // Cleanup quand le composant se démonte
    return () => {
      console.log(`🔴 [SIGNALR CLEANUP] Agent ${agent.id}: Removing listeners`);
      connection.off(connectedCallsEvent, onConnectedCallsReceived);
      connection.off(performanceEvent, onPerformanceDataReceived);
    };
  }, [connection, isConnected, agent.id]);

  // Libellé = activité Teams en priorité (« InACall », « Presenting »…), comme la console agent ;
  // pastille = disponibilité. L'ancien code n'utilisait QUE `availability`, si bien qu'un agent en
  // communication s'affichait « Busy » ici et « InACall » dans la console.
  const fallbackState = agent.state || agent.status || "";
  const stateLabel = presenceLabel(agentPresence, fallbackState, t);
  const badgeStatus = presenceBadge(agentPresence, fallbackState);
  const agentName = agent.name || agent.displayName || agent.email || t("REALTIME.UNKNOWN_AGENT");

  return (
    <div key={agent.id} className="agent-row" data-presence-status={badgeStatus}>
      <div className="agent-info-section">
        <Persona
          name={agentName}
          secondaryText={stateLabel}
          size="medium"
          image={{
            src: agentData.photoUrl || undefined,
          }}
          presence={{
            status: badgeStatus,
          }}
        />
      </div>
      
      <div className="agent-stats">
        <div className="stat-value">
          <span>{agentData.connectedCalls}</span>
        </div>
        <div className="stat-value">
          <span>{agentData.missedCalls}</span>
        </div>
        <div className="stat-value">
          <span>{agentData.totalCalls}</span>
        </div>
        <div className="stat-value">
          <span>{formatTime(agentData.totalCallsDuration)}</span>
        </div>
      </div>
    </div>
  );
};

export default AgentRow;
